import {AChartX} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent, useEffect, useState} from "react";
import {useTheme} from "@mui/material";
import {useAppSelector} from "../../../../Config/Hooks";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import {PowerStationCommandCount} from "../reactQuery/CommandCountByPowerStationReactQuery";

interface Props {
    data: PowerStationCommandCount[] | undefined;
    height?: number;
}

const PALETTE = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#5dadeb',
    '#8e44ad', '#16a085'
];

const VISIBLE_BARS = 30;

const CommandsByPowerStationBarChart: FunctionComponent<Props> = ({data, height = 380}) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const theme = useTheme();

    const sortedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return [...data].sort((a, b) => b.count - a.count);
    }, [data]);

    // @ts-ignore
    const option: AChartXProps['option'] = React.useMemo(() => {
        const total = sortedData.length;
        const showDataZoom = total > VISIBLE_BARS;
        const endPercent = total > 0 ? Math.min(100, (VISIBLE_BARS / total) * 100) : 100;

        return {
            color: PALETTE,
            toolbox: {show: false},
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                formatter: (params: any) => {
                    const item = Array.isArray(params) ? params[0] : params;
                    return `${item.name}<br/>Comandos: <b>${item.value}</b>`;
                }
            },
            grid: {
                containLabel: true,
                top: 16,
                bottom: showDataZoom ? 60 : 30,
                right: 30,
                left: 40,
            },
            xAxis: {
                type: 'category',
                data: sortedData.map(item => item.powerStationName),
                axisLabel: {
                    interval: 0,
                    rotate: 60,
                    formatter: (value: string) =>
                        value && value.length > 14 ? value.substring(0, 14) + '…' : value,
                }
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                splitLine: {
                    lineStyle: {type: 'dashed', color: '#e0e0e0'}
                },
                axisLine: {show: false},
                axisTick: {show: false}
            },
            dataZoom: showDataZoom ? [
                {type: 'slider', xAxisIndex: 0, start: 0, end: endPercent, height: 18, bottom: 10},
                {type: 'inside', xAxisIndex: 0, start: 0, end: endPercent}
            ] : [],
            series: [{
                name: 'Comandos',
                type: 'bar',
                colorBy: 'data',
                data: sortedData.map(item => item.count),
                itemStyle: {borderRadius: [4, 4, 0, 0]}
            }]
        };
    }, [sortedData]);

    const [content, setContent] = useState<JSX.Element>();
    useEffect(() => {
        if (sortedData.length > 0) {
            setContent(
                <AChartX
                    key={`commands-by-ps-chart-${openSubMenu}`}
                    option={option}
                    width={'100%'}
                    height={height}
                    loading={false}
                    theme={theme.palette.mode}
                />
            );
        } else {
            setContent(<DataNotFound boxStyle={{height, width: '100%'}}/>);
        }
    }, [sortedData, openSubMenu, option, height, theme.palette.mode]);

    return <>{content}</>;
};

export default CommandsByPowerStationBarChart;
